import { Component, HostListener, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { SHARED_MODULES } from 'src/app/modules/shared-imports';
import { PermissionService } from 'src/app/services/permission.service';
import { SimpleCalculatorComponent } from 'src/app/commons/components/simple-calculator/simple-calculator.component';
import { KeyboardShortcutsService } from 'src/app/commons/keyboard-shortcuts/keyboard-shortcuts.service';
import { SideBarMenuService } from 'src/app/services/side-bar-menu.service';
import { AiChatWidgetSharedService } from 'src/app/services/ai-chat-widget-shared.service';
import { TranslationService } from 'src/app/i18n/translation.service';
import { RoleMenuItem } from 'src/app/interfaces/role-menu-item';
import { normalizeMenuRouterLink } from 'src/app/commons/utils/menu-router-link';
import {
  GlobalSearchService,
  GlobalSearchResponse,
  GlobalSearchResult,
} from './global-search.service';

interface MenuSearchEntry {
  rawLabel: string;
  parentRawLabel: string | null;
  route: string;
}

interface GlobalSearchGroup {
  categoryKey: string;
  icon: string;
  items: GlobalSearchResult[];
}

const MIN_TERM_LENGTH = 2;
const RECENT_SEARCH_KEY = 'globalSearch.recent';
const RECENT_SEARCH_MAX = 5;

/** True only when the whole (whitespace-stripped) string is digits/operators/parens AND
 *  contains at least one operator — a bare number ("123") is left alone since that's far more
 *  likely a real reference code someone is searching for than a calculation. */
function isMathExpression(text: string): boolean {
  const t = text.replace(/\s+/g, '');
  if (!t || !/^[0-9+\-*/×÷().]+$/.test(t)) return false;
  return /[+\-*/×÷]/.test(t);
}

/** Small recursive-descent evaluator (standard operator precedence, parens, unary +/-, decimals)
 *  — deliberately not eval()/Function() (never run arbitrary text as code), and deliberately not
 *  reusing SimpleCalculatorComponent's left-to-right compute() (that one intentionally mimics a
 *  physical calculator's no-precedence behavior; typed math in a search box is expected to follow
 *  normal order of operations, like macOS Spotlight). Returns null for anything invalid
 *  (trailing garbage, divide-by-zero, etc.) rather than showing a misleading result. */
function evaluateMathExpression(input: string): number | null {
  const expr = input.replace(/×/g, '*').replace(/÷/g, '/').replace(/\s+/g, '');
  if (!expr) return null;
  let pos = 0;
  const peek = () => expr[pos];
  const consume = () => expr[pos++];

  function parseExpression(): number {
    let value = parseTerm();
    while (peek() === '+' || peek() === '-') {
      const op = consume();
      const rhs = parseTerm();
      value = op === '+' ? value + rhs : value - rhs;
    }
    return value;
  }
  function parseTerm(): number {
    let value = parseFactor();
    while (peek() === '*' || peek() === '/') {
      const op = consume();
      const rhs = parseFactor();
      value = op === '*' ? value * rhs : value / rhs;
    }
    return value;
  }
  function parseFactor(): number {
    if (peek() === '+') { consume(); return parseFactor(); }
    if (peek() === '-') { consume(); return -parseFactor(); }
    if (peek() === '(') {
      consume();
      const value = parseExpression();
      if (peek() === ')') consume();
      return value;
    }
    const start = pos;
    while (pos < expr.length && /[0-9.]/.test(expr[pos])) pos++;
    if (pos === start) throw new Error('unexpected token');
    return parseFloat(expr.slice(start, pos));
  }

  try {
    const result = parseExpression();
    if (pos !== expr.length) return null; // trailing garbage — not a clean expression
    return isFinite(result) ? Math.round(result * 1e9) / 1e9 : null;
  } catch {
    return null;
  }
}

/** category -> [i18n key, PrimeIcons class]. Kept as a plain object (not a static class field) so
 *  the template can read it via the component instance without a static-member indirection. */
const CATEGORY_META: Record<GlobalSearchResult['category'], { key: string; icon: string }> = {
  TRANSPORT_UNIT: { key: 'globalSearch.category.transportUnit', icon: 'pi pi-truck' },
  SHIPMENT: { key: 'globalSearch.category.shipment', icon: 'pi pi-box' },
  BUSINESS_PARTNER: { key: 'globalSearch.category.businessPartner', icon: 'pi pi-users' },
  PRODUCT: { key: 'globalSearch.category.product', icon: 'pi pi-tag' },
  LOADING_SITE: { key: 'globalSearch.category.loadingSite', icon: 'pi pi-map-marker' },
  USER: { key: 'globalSearch.category.user', icon: 'pi pi-user' },
  MENU_ITEM: { key: 'globalSearch.category.menuItem', icon: 'pi pi-compass' },
};

/** Cross-entity "find anything by name/reference number" search (Stock Units, Consignments,
 *  Business Partners, Products, Locations, Users) — deliberately separate from Quick Search
 *  (`commons/quick-search/`), which is a specialized inventory-dimension finder, not a generic
 *  record search. Own trigger/shortcut in the header, own GLOBAL_SEARCH_ACCESS permission. */
@Component({
  selector: 'app-global-search',
  templateUrl: './global-search.component.html',
  styleUrls: ['./global-search.component.css'],
  standalone: true,
  imports: [SHARED_MODULES, SimpleCalculatorComponent],
})
export class GlobalSearchComponent implements OnInit {
  /** True when mounted inside app-unified-search's Records tab (Part A of the search redesign) —
   *  suppresses this component's own trigger button, outer dialog, input row, and nested
   *  calculator dialog, all of which the shell now owns. Defaults false so standalone usage (if
   *  this component is ever mounted anywhere else) keeps working exactly as before — nothing was
   *  deleted, only made conditional. */
  @Input() embedded = false;

  visible = false;
  queryText = '';
  searching = false;
  searched = false;
  groups: GlobalSearchGroup[] = [];
  highlightedIndex = -1;

  // ── Recent searches — same personal, browser-local, capped-at-5 convenience list as Quick
  // Search (see quick-search.component.ts's own field comment for why: captured on close, not
  // per keystroke, so a half-typed fragment never pollutes the list).
  recentSearches: string[] = [];

  // ── Help — a short static "what can I search / how" panel. Global Search has no query
  // grammar to teach (unlike Quick Search's dimension parser), so this is just a category list
  // plus the Ctrl+K shortcut, not a tap-to-try guide.
  showHelp = false;
  readonly helpCategories = Object.values(CATEGORY_META);

  // ── Calculator — a general-purpose 4-function calculator, reachable from anywhere via this
  // already-open, Ctrl+K-triggered dialog rather than adding a 7th permanent header icon (see
  // the placement discussion this was decided from). Blank/no prefill, unlike Quick Search's own
  // per-row calculator launch, which seeds a real quoted price.
  calculatorDialogVisible = false;
  /** Prefill for the calculator when opened from a computed math result (see openCalculatorFromMath) —
   *  null for the plain "blank tool" launch from the toolbar icon/shortcut. */
  calculatorPrefill: number | null = null;

  // ── Inline math (macOS Spotlight-style) — set instead of `groups` when the typed text is a
  // pure arithmetic expression; the backend search is skipped entirely in that case (see
  // isMathExpression/evaluateMathExpression above and the ngOnInit pipeline below).
  mathResult: number | null = null;

  private textChange$ = new Subject<string>();

  /** Flattened, RBAC-already-filtered sidebar menu (raw/untranslated labels — translated at
   *  search time via translationService.instant() so a language switch is picked up live, not
   *  cached stale). Fetched once via the same /api/v1/menu-items call SideBarComponent uses —
   *  see buildMenuGroup()'s own note on why this isn't shared state with the sidebar. */
  private menuSearchIndex: MenuSearchEntry[] = [];

  constructor(
    private globalSearchService: GlobalSearchService,
    public permissionService: PermissionService,
    private router: Router,
    private keyboardShortcutsService: KeyboardShortcutsService,
    private sideBarMenuService: SideBarMenuService,
    private translationService: TranslationService,
    private aiChatWidgetSharedService: AiChatWidgetSharedService,
  ) {}

  /** Help panel's "View all shortcuts" link — hands off to the sibling KeyboardShortcutsComponent
   *  (mounted alongside this one in the header) via a shared service, closing this dialog first so
   *  the two overlays don't stack. */
  openAllShortcuts(): void {
    this.close();
    this.keyboardShortcutsService.requestOpen();
  }

  ngOnInit(): void {
    this.loadRecentSearches();
    this.sideBarMenuService.getSideBarMenu().subscribe({
      next: (data: any) => { this.menuSearchIndex = this.buildMenuSearchIndex(data ?? []); },
      error: () => { this.menuSearchIndex = []; },
    });
    this.textChange$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(text => {
        const trimmed = (text || '').trim();
        this.mathResult = null;
        if (isMathExpression(trimmed)) {
          this.mathResult = evaluateMathExpression(trimmed);
          this.searching = false;
          this.searched = true;
          this.groups = [];
          return of(null); // pure arithmetic isn't a record search — skip the API call entirely
        }
        if (trimmed.length < MIN_TERM_LENGTH) {
          this.searching = false;
          this.searched = false;
          this.groups = [];
          return of(null);
        }
        this.searching = true;
        return this.globalSearchService.search(trimmed).pipe(
          switchMap(response => of({ response, term: trimmed })),
        );
      }),
    ).subscribe({
      next: result => { if (result) this.applyResult(result.response, result.term); },
      error: () => { this.groups = []; this.searching = false; this.searched = true; },
    });
  }

  /** Recursively flattens the RBAC-already-filtered menu tree into a searchable list — header
   *  items with no navigable routerLink of their own are skipped, but their children are still
   *  visited. Labels are kept RAW (translation keys / literal text) here — translated only at
   *  search time in buildMenuGroup(), so a language switch is reflected live. */
  private buildMenuSearchIndex(items: RoleMenuItem[], parentRawLabel: string | null = null): MenuSearchEntry[] {
    const flat: MenuSearchEntry[] = [];
    for (const item of items ?? []) {
      const route = normalizeMenuRouterLink((item as any).routerLink);
      if (item.label && route) {
        flat.push({ rawLabel: item.label as string, parentRawLabel, route });
      }
      if (item.items?.length) {
        flat.push(...this.buildMenuSearchIndex(item.items, (item.label as string) ?? parentRawLabel));
      }
    }
    return flat;
  }

  /** Client-side-only "Pages" result group — matches sidebar menu labels the current user can
   *  actually see (menuSearchIndex already came from the RBAC-filtered /api/v1/menu-items
   *  response, same as the sidebar itself). Deliberately a separate fetch from SideBarComponent's
   *  own copy rather than shared state — see menuSearchIndex's own field comment. */
  private buildMenuGroup(term: string): GlobalSearchGroup | null {
    const lower = term.toLowerCase();
    const items: GlobalSearchResult[] = this.menuSearchIndex
      .map(m => ({ ...m, label: this.translationService.instant(m.rawLabel) }))
      .filter(m => m.label.toLowerCase().includes(lower))
      .slice(0, 5)
      .map(m => ({
        id: 0,
        encodedId: null,
        title: m.label,
        subtitle: m.parentRawLabel ? this.translationService.instant(m.parentRawLabel) : null,
        category: 'MENU_ITEM' as const,
        route: m.route,
        amount: null,
        paymentStatus: null,
      }));
    if (!items.length) return null;
    const meta = CATEGORY_META['MENU_ITEM'];
    return { categoryKey: meta.key, icon: meta.icon, items };
  }

  /** Quick Search's "Ask Linc" fallback card — closes this dialog and hands the typed text to
   *  the globally-mounted AiChatWidgetComponent via the shared service (see its own doc comment
   *  on why this indirection instead of a direct method call). */
  askLinc(): void {
    const question = this.queryText.trim();
    this.close();
    this.aiChatWidgetSharedService.requestOpenWithQuestion(question);
  }

  get canUse(): boolean {
    return this.permissionService.hasEffective('GLOBAL_SEARCH_ACCESS');
  }

  get minCharsHint(): boolean {
    return this.mathResult == null
      && this.queryText.trim().length > 0
      && this.queryText.trim().length < MIN_TERM_LENGTH;
  }

  /** Ctrl+K / Cmd+K — the ubiquitous command-palette convention (Linear/GitHub/Slack/VS Code),
   *  deliberately not "/", which Quick Search already owns. Ignored while typing in any
   *  input/textarea/select/contenteditable, while Global Search is already open, or without
   *  GLOBAL_SEARCH_ACCESS (matches the trigger button's own gating). */
  @HostListener('document:keydown', ['$event'])
  handleShortcut(event: KeyboardEvent): void {
    if (this.embedded) return; // shell owns Ctrl+K when merged — see the `embedded` field comment
    if (event.key.toLowerCase() !== 'k' || !(event.ctrlKey || event.metaKey) || event.altKey) return;
    if (this.visible || !this.canUse) return;
    const target = event.target as HTMLElement | null;
    const tag = target?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable) return;
    event.preventDefault();
    this.open();
  }

  open(): void {
    this.visible = true;
    this.showHelp = false;
  }

  close(): void {
    this.saveRecentSearch(this.queryText);
    this.visible = false;
    this.queryText = '';
    this.groups = [];
    this.mathResult = null;
    this.searched = false;
    this.searching = false;
    this.highlightedIndex = -1;
    this.showHelp = false;
    this.calculatorDialogVisible = false;
  }

  toggleHelp(): void {
    this.showHelp = !this.showHelp;
  }

  openCalculator(): void {
    this.calculatorPrefill = null;
    this.calculatorDialogVisible = true;
  }

  /** Tapping a computed inline-math result opens the full calculator prefilled with that answer,
   *  so a quick "150*3" can seamlessly continue into a longer calculation. */
  openCalculatorFromMath(): void {
    if (this.mathResult == null) return;
    this.calculatorPrefill = this.mathResult;
    this.calculatorDialogVisible = true;
  }

  closeCalculator(): void {
    this.calculatorDialogVisible = false;
  }

  /** A second, standalone shortcut (Ctrl+Alt+C) that opens the calculator directly — without
   *  first opening the full search box — for someone who only ever wants the calculator and
   *  never the record search. Deliberately not Ctrl+Shift+C (Chrome DevTools element inspector)
   *  or reusing Ctrl+K (that's Global Search's own open shortcut, see handleShortcut above). */
  @HostListener('document:keydown', ['$event'])
  handleCalculatorShortcut(event: KeyboardEvent): void {
    if (this.embedded) return; // shell owns Ctrl+Alt+C when merged
    // event.key can be undefined for some synthetic/extension-dispatched KeyboardEvents —
    // confirmed via a real prod error (TypeError: Cannot read properties of undefined
    // (reading 'toLowerCase')) before this guard existed.
    if ((event.key || '').toLowerCase() !== 'c' || !event.ctrlKey || !event.altKey) return;
    if (this.calculatorDialogVisible || !this.canUse) return;
    const target = event.target as HTMLElement | null;
    const tag = target?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable) return;
    event.preventDefault();
    this.openCalculator();
  }

  onTextChange(): void {
    this.highlightedIndex = -1;
    this.textChange$.next(this.queryText);
  }

  /** Called by the shell (embedded mode only) instead of the removed ngModel binding — reuses
   *  the exact same debounced pipeline a typed keystroke would trigger, so nothing about how
   *  results are fetched changes based on where the text came from. */
  setQuery(text: string): void {
    this.queryText = text;
    this.onTextChange();
  }

  get showRecent(): boolean {
    return !this.queryText.trim() && !this.showHelp && this.recentSearches.length > 0;
  }

  /** A recent pill re-runs the exact same debounced pipeline as typing — no separate search
   *  call to keep in sync with. */
  useRecentSearch(text: string): void {
    this.queryText = text;
    this.onTextChange();
  }

  removeRecentSearch(text: string, event: Event): void {
    event.stopPropagation();
    this.recentSearches = this.recentSearches.filter(t => t !== text);
    this.persistRecentSearches();
  }

  private loadRecentSearches(): void {
    try {
      const raw = localStorage.getItem(RECENT_SEARCH_KEY);
      this.recentSearches = raw ? JSON.parse(raw) : [];
    } catch {
      this.recentSearches = [];
    }
  }

  private saveRecentSearch(text: string): void {
    const trimmed = (text || '').trim();
    if (trimmed.length < MIN_TERM_LENGTH) return;
    this.recentSearches = [trimmed, ...this.recentSearches.filter(t => t !== trimmed)].slice(0, RECENT_SEARCH_MAX);
    this.persistRecentSearches();
  }

  private persistRecentSearches(): void {
    try {
      localStorage.setItem(RECENT_SEARCH_KEY, JSON.stringify(this.recentSearches));
    } catch { /* localStorage unavailable (private browsing etc.) — recent list just won't persist */ }
  }

  private applyResult(result: GlobalSearchResponse, term: string): void {
    this.searching = false;
    this.searched = true;
    const order: Array<[keyof GlobalSearchResponse, GlobalSearchResult['category']]> = [
      ['transportUnits', 'TRANSPORT_UNIT'],
      ['shipments', 'SHIPMENT'],
      ['businessPartners', 'BUSINESS_PARTNER'],
      ['products', 'PRODUCT'],
      ['loadingSites', 'LOADING_SITE'],
      ['users', 'USER'],
    ];
    const groups = order
      .map(([field, category]) => {
        const items = result?.[field];
        if (!items || !items.length) return null;
        const meta = CATEGORY_META[category];
        return { categoryKey: meta.key, icon: meta.icon, items } as GlobalSearchGroup;
      })
      .filter((g): g is GlobalSearchGroup => g !== null);
    // Menu-item matches render LAST, after every real record category, so hard record matches
    // keep visual priority — see buildMenuGroup()'s own doc comment.
    const menuGroup = this.buildMenuGroup(term);
    if (menuGroup) groups.push(menuGroup);
    this.groups = groups;
    // Auto-highlight the first result the moment results arrive — every command-palette this UI
    // is modeled on (VS Code Cmd+P, Spotlight, Slack quick switcher) does this so Enter always
    // does something sensible without first pressing an arrow key. Before this, highlightedIndex
    // stayed -1 until an explicit ArrowDown, so a visible row (e.g. mouse hover) meant nothing to
    // Enter's own check (`highlightedIndex >= 0`) — real report 2026-09-24: "pressing enter
    // should open the screen... not working though it's coded." The code was correct; nothing
    // had ever set a real selection for Enter to act on.
    this.highlightedIndex = groups.length ? 0 : -1;
  }

  get flatResults(): GlobalSearchResult[] {
    return this.groups.flatMap(g => g.items);
  }

  get hasResults(): boolean {
    return this.groups.length > 0;
  }

  select(item: GlobalSearchResult): void {
    this.close();
    this.router.navigateByUrl(item.route);
  }

  onInputKeydown(event: KeyboardEvent): void {
    // While the calculator overlay is open, it owns all keyboard input (digits, Enter, Escape,
    // etc. — see SimpleCalculatorComponent.onKeydown) — the underlying search box's own
    // arrow/Enter/Escape handling must stay out of the way, or the same keypress could both
    // drive the calculator AND close the whole Global Search dialog underneath it.
    if (this.calculatorDialogVisible) return;
    if (this.mathResult !== null && event.key === 'Enter') {
      event.preventDefault();
      this.openCalculatorFromMath();
      return;
    }
    const flat = this.flatResults;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (flat.length) this.highlightedIndex = (this.highlightedIndex + 1) % flat.length;
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (flat.length) this.highlightedIndex = (this.highlightedIndex - 1 + flat.length) % flat.length;
    } else if (event.key === 'Enter') {
      if (this.highlightedIndex >= 0 && this.highlightedIndex < flat.length) {
        event.preventDefault();
        this.select(flat[this.highlightedIndex]);
      }
    } else if (event.key === 'Escape') {
      this.close();
    }
  }

  isHighlighted(item: GlobalSearchResult): boolean {
    return this.flatResults[this.highlightedIndex] === item;
  }

  /** SHIPMENT rows only — same colour mapping the shipment edit form's own payment status pills
   *  already use (shipments.component.html), covering both SalesPaymentStatus and
   *  PurchasePaymentStatus value sets since a shipment only ever carries one of the two. */
  paymentStatusSeverity(status: string | null): string {
    switch (status) {
      case 'FULLY_PAID':
      case 'PAID':
        return 'success';
      case 'PARTIALLY_PAID':
      case 'PENDING':
        return 'warning';
      case 'OVERDUE':
      case 'DISPUTED':
      case 'CANCELLED':
        return 'danger';
      default:
        return 'info';
    }
  }
}
